import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  memo,
  useMemo,
} from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { Feather, Ionicons, MaterialIcons } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import { observer } from "mobx-react";
import {
  BlockPlanificationConstants,
  WorkoutLimits,
} from "../../../Resources/Constants";
import { v4 as uuidv4 } from "uuid";

import Exercise from "../../Firestore/Exercise";
import Serie from "../../Firestore/Serie";
import Attribute from "../../Firestore/Attribute";

import Tooltip from "../../Components/Tooltip";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import CustomAlert from "../../Components/CustomAlert";

import ExerciseCard from "./ExerciseCard";
import ExerciseSelectionPlaceholder from "./ExerciseSelectionPlaceholder";
import {
  openFloatingSearch,
  closeFloatingSearch,
} from "../../Exercise/Search/GlobalExerciseSearchFloating";
import FloatingMenu from "../../Components/FloatingMenu";
import {
  DraggableExercise,
  EmptyWorkoutDropZone,
  TrailingDropZone,
} from "./exerciseDrag/ExerciseDrag";
import { useToast } from "../../contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { useDevice } from "../../_layout";
import { markTutorialStepCompleted } from "../../utils/tutorialUtils";

const EmptyView = ({
  onAddExercise,
  onPasteExercises,
  pasteButtonLabel,
  copiedExercisesCount = 0,
  t,
}) => {
  return (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>{t("noExercisesYet-string")}</Text>
      <Text style={styles.emptySubtitle}>
        {t("addOneOrPasteFromClipboard-string")}
      </Text>

      <View style={styles.emptyActionsRow}>
        <HoverableTouchableOpacity
          style={styles.emptyPrimaryBtn}
          hoverStyle={styles.btnHover}
          onPress={onAddExercise}
          title={t("addExercise-string")}
        >
          <Ionicons name="add" size={16} color={Colors.bubbleColor} />
          <Text style={styles.emptyPrimaryBtnText}>
            {t("addExercise-string")}
          </Text>
        </HoverableTouchableOpacity>

        {copiedExercisesCount > 0 && (
          <Tooltip content={pasteButtonLabel} placement="top">
            <HoverableTouchableOpacity
              style={styles.footerPasteExerciseBtn}
              hoverStyle={styles.btnHover}
              onPress={() => onPasteExercises?.()}
              title={pasteButtonLabel}
            >
              <Ionicons
                name="clipboard-outline"
                size={16}
                color={Colors.bubbleColor}
              />
            </HoverableTouchableOpacity>
          </Tooltip>
        )}
      </View>
    </View>
  );
};

// Helper to normalize protocol slot
const normalizeProtocolSlot = (protocolSlot) => {
  if (typeof protocolSlot !== "string") return null;
  const trimmed = protocolSlot.trim();
  return trimmed ? trimmed : null;
};

const WorkoutCard = observer(
  ({
    workout,
    block,
    coach,
    t,
    formatWorkoutDate,
    handleDuplicateWorkout,
    exitWorkoutEditMode,
    persistBlockChange,
    handleCopyExercise,
    handlePasteExercise,
    handleDeleteWorkout,
    showSetPriority = false,
    rirOrRpe = false,
    isWeekHidden = false,
    exercisesExpanded = true,
    showExerciseHistory = true,
  }) => {
    const hasExercises =
      Array.isArray(workout?.exercises) && workout.exercises.length > 0;
    const copyWorkoutDisabled = !hasExercises;
    const isEditMode =
      block?.blockViewEditingWorkoutId === String(workout?.id).toLowerCase() &&
      hasExercises;
    const focusRequest = workout?.exerciseFocusRequest ?? null;
    // Access block.exercisesExpanded directly for MobX reactivity
    const isExercisesExpanded =
      block?.exercisesExpanded ?? exercisesExpanded ?? true;
    const blockHasProtocolSlots = !!block?.workouts?.some(
      (item) => item?.hasProtocolSlotExercises
    );
    const showOnlyProtocolSlots =
      coach?.showOnlyProtocolSlots === true && blockHasProtocolSlots;

    const [isEditingWorkoutName, setIsEditingWorkoutName] = useState(false);
    const [workoutNameDraft, setWorkoutNameDraft] = useState(
      typeof workout?.name === "string" ? workout.name : ""
    );
    const workoutNameOriginalRef = useRef(
      typeof workout?.name === "string" ? workout.name : ""
    );
    const workoutNameDraftRef = useRef(
      typeof workout?.name === "string" ? workout.name : ""
    );
    const workoutNameInputRef = useRef(null);
    const skipNextWorkoutNameCommitRef = useRef(false);
    const skipNextWorkoutNameBlurCommitRef = useRef(false);
    const pendingWorkoutNameBlurTimerRef = useRef(null);
    const copiedExercises = Array.isArray(coach?.copiedExercises)
      ? coach.copiedExercises
      : [];
    const pasteExercisesDisabled = copiedExercises.length === 0;
    const pasteButtonLabel =
      copiedExercises.length === 1
        ? t("pasteExercise-string")
        : t("pasteExercises-string");

    const sortedExercises = Array.isArray(workout?.exercises)
      ? workout.exercises
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      : [];

    const onlySlotsDisplayItems = showOnlyProtocolSlots
      ? (() => {
          const items = [];
          let index = 0;

          while (index < sortedExercises.length) {
            const exercise = sortedExercises[index];
            const isProtocol = exercise?.protocolSlot != null;

            if (isProtocol) {
              items.push({ type: "exercise", exercise, index });
              index += 1;
              continue;
            }

            const runStart = index;
            while (
              index < sortedExercises.length &&
              sortedExercises[index]?.protocolSlot == null
            ) {
              index += 1;
            }

            const count = index - runStart;
            items.push({
              type: "placeholder",
              count,
              key: `hidden-${workout?.id ?? "workout"}-${runStart}`,
            });
          }

          return items;
        })()
      : null;

    const [isAddingExercise, setIsAddingExercise] = useState(false);
    const [addExerciseAnchorNode, setAddExerciseAnchorNode] = useState(null);
    const shouldAutoOpenAddExerciseSearchRef = useRef(false);
    const [isMoreHovered, setIsMoreHovered] = useState(false);
    const [isWorkoutMenuOpen, setIsWorkoutMenuOpen] = useState(false);
    const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
    const { showToast } = useToast();
    const { i18n } = useTranslation();
    const { isMobile, isIOS } = useDevice();

    const getExerciseName = useCallback(
      (exercise) =>
        exercise?.getName?.(t, i18n.language) ||
        exercise?.name ||
        t("exercise-string"),
      [t, i18n.language]
    );

    const getWorkoutName = useCallback(
      (targetWorkout) => {
        if (!targetWorkout || !block?.workouts) return "";
        const week = targetWorkout?.week ?? 1;
        const sameWeekWorkouts = block.workouts
          .filter((w) => w.week === week)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const workoutIndex = sameWeekWorkouts.findIndex(
          (w) => w.id === targetWorkout.id
        );
        const workoutNumber =
          workoutIndex > -1
            ? workoutIndex + 1
            : (targetWorkout?.order ?? 0) + 1;
        const workoutName =
          typeof targetWorkout?.name === "string"
            ? targetWorkout.name.trim()
            : "";
        const workoutLabel =
          workoutName ||
          t("workoutWithNumber-string", {
            number: workoutNumber,
          });

        return workoutLabel;
      },
      [block?.workouts, block?.getWeekName, t]
    );

    const getWeekLabel = useCallback(
      (week) => {
        if (!week && week !== 0) return "";
        const customWeekName = block?.getWeekName?.(week) || "";
        return customWeekName.trim() !== ""
          ? customWeekName
          : t("weekWithNumber-string", { week });
      },
      [block?.getWeekName, t]
    );

    const getExercisePosition = useCallback((targetWorkout, exerciseId) => {
      if (!targetWorkout?.exercises || !exerciseId) return null;
      const idx = targetWorkout.exercises.findIndex(
        (ex) => ex?.id === exerciseId
      );
      return idx > -1 ? idx + 1 : null;
    }, []);

    // Calculate if workout can move up or down
    let canMoveUp = false;
    let canMoveDown = false;
    if (
      block?.workouts &&
      workout &&
      workout.week !== null &&
      workout.week !== undefined
    ) {
      const sameWeekWorkouts = block.workouts
        .filter((w) => w.week === workout.week)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const index = sameWeekWorkouts.findIndex((w) => w.id === workout.id);
      if (index > -1 && sameWeekWorkouts.length > 1) {
        canMoveUp = index > 0;
        canMoveDown = index < sameWeekWorkouts.length - 1;
      }
    }

    const workoutMoreMenuItems = useMemo(() => {
      const items = [
        {
          key: "exportToLibrary",
          label: t("exportToLibrary-string"),
          icon: { type: "ionicons", name: "book-outline" },
        },
      ];

      // Add "delete from calendar" option only if workout has a date
      if (workout?.date !== null && workout?.date !== undefined) {
        items.push({
          key: "deleteFromCalendar",
          label: t("deleteWorkoutFromCalendar-string"),
          icon: { type: "ionicons", name: "calendar-outline" },
        });
      }

      return items;
    }, [workout?.date, t]);

    const handleWorkoutMenuItemAction = useCallback(
      (itemKey) => {
        if (itemKey === "deleteFromCalendar") {
          if (!workout) return;
          workout.setProperty("date", null);
          workout.setProperty("hasChanged", true);
          persistBlockChange?.();
        } else if (itemKey === "exportToLibrary") {
          if (!workout) return;
          workout.copyToLibrary(showToast);
        }
      },
      [workout, persistBlockChange, showToast]
    );

    const getAddExerciseInstanceId = useCallback(() => {
      const blockId = block?.id || "unknown";
      return `workout-add-search-${blockId}-${workout?.id || "unknown"}`;
    }, [block?.id, workout?.id]);

    const closeAddExerciseFlow = useCallback(() => {
      const instanceId = getAddExerciseInstanceId();
      closeFloatingSearch(coach, instanceId);
      setIsAddingExercise(false);
      setAddExerciseAnchorNode(null);
      shouldAutoOpenAddExerciseSearchRef.current = false;
    }, [coach, getAddExerciseInstanceId]);

    const handleAddExerciseFromSearch = useCallback(
      (searchPayload = null) => {
        if (!workout) return;
        const keepOpen = searchPayload?.keepOpen === true;

        const existing = Array.isArray(workout?.exercises)
          ? workout.exercises
          : [];

        // Check exercise limit per workout
        if (existing.length >= WorkoutLimits.MAX_EXERCISES_PER_WORKOUT) {
          showToast("maxExercisesPerWorkoutReached-string", {
            type: "warning",
            title: "maxExercisesPerWorkoutReached-string",
            description: "maxExercisesPerWorkoutDescription-string",
            variables: { limit: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT },
          });
          closeAddExerciseFlow();
          return;
        }

        const newOrder =
          existing.length > 0
            ? Math.max(...existing.map((ex) => ex?.order ?? 0)) + 1
            : 0;

        const exerciseName =
          typeof searchPayload?.exerciseName === "string"
            ? searchPayload.exerciseName
            : "";
        const exerciseOriginalName =
          typeof searchPayload?.exerciseOriginalName === "string"
            ? searchPayload.exerciseOriginalName
            : "";

        const newExercise = new Exercise(
          uuidv4(),
          exerciseName,
          exerciseOriginalName,
          "",
          newOrder,
          true,
          false,
          workout,
          [],
          [],
          "",
          null,
          ""
        );

        for (let i = 0; i < 3; i++) {
          const newSerie = new Serie(
            uuidv4(),
            i,
            0,
            0,
            0,
            "",
            "",
            "",
            newExercise,
            3
          );
          newExercise.series.push(newSerie);
        }

        if (
          Array.isArray(searchPayload?.modifiers) &&
          searchPayload.modifiers.length
        ) {
          const EXCLUSIVE_TITLES = new Set([
            Attribute.AttributeTitles.equipment,
            Attribute.AttributeTitles.tempo,
            Attribute.AttributeTitles.rom,
            Attribute.AttributeTitles.stanceGrip,
          ]);

          searchPayload.modifiers.forEach((modifier) => {
            if (!modifier) return;
            const originalName = String(modifier.originalName || "").trim();
            if (!originalName) return;

            const title = String(modifier.type || "").trim();
            const subtitle = modifier.subtype ? String(modifier.subtype) : "";

            if (title && EXCLUSIVE_TITLES.has(title)) {
              newExercise.attributes = (newExercise.attributes || []).filter(
                (a) => (a?.title || "") !== title
              );
            }

            newExercise.attributes.push(
              new Attribute(
                undefined,
                originalName,
                title,
                subtitle,
                0,
                false,
                false,
                null,
                newExercise
              )
            );
          });
        }

        // Mark tutorial step as completed if this is the first exercise added
        // and the step hasn't been completed yet
        // Check BEFORE pushing the new exercise, since existing is a reference
        const isFirstExercise = existing.length === 0;

        workout.exercises.push(newExercise);
        if (coach?.showOnlyProtocolSlots && newExercise?.protocolSlot == null) {
          coach.showOnlyProtocolSlots = false;
        }
        if (!keepOpen) {
          workout.setProperty("exerciseFocusRequest", {
            exerciseId: newExercise.id,
            field: "protocol",
          });
        }
        workout.setProperty("hasChanged", true);
        // Set workout to edit mode when adding an exercise
        block?.setBlockViewEditingWorkoutId?.(workout.id);

        if (isFirstExercise) {
          markTutorialStepCompleted(coach, showToast);
        }

        persistBlockChange?.();
        if (!keepOpen) {
          closeAddExerciseFlow();
        }
      },
      [workout, block, persistBlockChange, closeAddExerciseFlow, showToast]
    );

    const openAddExerciseSearch = useCallback(() => {
      if (!coach?.setProperty) return;
      const instanceId = getAddExerciseInstanceId();

      openFloatingSearch(coach, {
        instanceId,
        initialSearchText: "",
        referenceElement: addExerciseAnchorNode,
        onSelectExercise: handleAddExerciseFromSearch,
        onClose: closeAddExerciseFlow,
        onRequestAddExercise: startAddExercise,
        suggestionContext: null,
        crossAxisOffset: 0,
        slotExercise: null,
        primarySlotExercise: null,
        block,
        protocolSlot: null,
        currentWeek: null,
        suggestionsMode: "workout",
        currentWorkout: workout,
        exerciseIndex: workout?.exercises?.length ?? 0,
        allowShiftEnterReopen: true,
      });
    }, [
      coach,
      block,
      workout,
      addExerciseAnchorNode,
      getAddExerciseInstanceId,
      closeAddExerciseFlow,
      handleAddExerciseFromSearch,
    ]);

    const startAddExercise = useCallback(() => {
      if (!workout) return;
      workout?.setProperty?.("exerciseFocusRequest", null);
      block?.setBlockViewEditingWorkoutId?.(workout.id);
      setIsAddingExercise(true);
      setAddExerciseAnchorNode(null);
      shouldAutoOpenAddExerciseSearchRef.current = true;
    }, [workout, block]);

    const cancelAddExercise = useCallback(() => {
      closeAddExerciseFlow();
    }, [closeAddExerciseFlow]);

    useEffect(() => {
      if (!isAddingExercise) return;
      if (!shouldAutoOpenAddExerciseSearchRef.current) return;
      if (!addExerciseAnchorNode) return;
      openAddExerciseSearch();
      shouldAutoOpenAddExerciseSearchRef.current = false;
    }, [isAddingExercise, addExerciseAnchorNode, openAddExerciseSearch]);

    useEffect(() => {
      return () => {
        if (!coach || !isAddingExercise) return;
        const instanceId = getAddExerciseInstanceId();
        if (coach.activeFloatingSearchId === instanceId) {
          closeAddExerciseFlow();
        }
      };
    }, [
      coach,
      isAddingExercise,
      getAddExerciseInstanceId,
      closeAddExerciseFlow,
    ]);

    const requestExerciseEditFocus = (
      exercise,
      field,
      serieId,
      fromDirectClick = false
    ) => {
      if (!workout || !exercise || !field) return;
      block?.setBlockViewEditingWorkoutId?.(workout.id);
      workout?.setProperty?.("exerciseFocusRequest", {
        exerciseId: exercise.id,
        field,
        serieId,
        fromDirectClick,
      });
    };

    const clearExerciseFocusRequest = () => {
      workout?.setProperty?.("exerciseFocusRequest", null);
    };

    useEffect(() => {
      if (!workout) return;
      if (!focusRequest || focusRequest.action !== "addExercise") return;
      startAddExercise();
      clearExerciseFocusRequest();
    }, [focusRequest, workout, startAddExercise, clearExerciseFocusRequest]);

    // Handle reordering exercises within the same workout
    const handleMoveExercise = useCallback(
      (fromIndex, toIndex, exerciseId) => {
        if (!workout) return;
        if (fromIndex === toIndex) return;
        const fallbackExercise =
          Array.isArray(workout.exercises) && workout.exercises[fromIndex]
            ? workout.exercises[fromIndex]
            : null;
        const movedExercise = exerciseId
          ? workout.exercises.find((ex) => ex?.id === exerciseId)
          : fallbackExercise;
        if (workout.reorderExercise) {
          workout.reorderExercise(fromIndex, toIndex);
        }
        persistBlockChange?.();
        if (movedExercise) {
          const position =
            getExercisePosition(workout, movedExercise?.id) ?? toIndex + 1;
          showToast("exerciseMovedToastTitle-string", {
            type: "info",
            title: "exerciseMovedToastTitle-string",
            description: "exerciseMovedToastDescriptionSameWorkout-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: {
              exerciseName: getExerciseName(movedExercise),
              position,
            },
          });
        }
      },
      [
        workout,
        persistBlockChange,
        showToast,
        getExerciseName,
        getWorkoutName,
        getWeekLabel,
        getExercisePosition,
        t,
      ]
    );

    // Handle moving exercises between workouts
    const handleMoveExerciseCrossWorkout = useCallback(
      (sourceWorkoutId, exerciseId, targetWorkoutId, targetIndex) => {
        if (!block?.workouts) return;
        if (sourceWorkoutId === targetWorkoutId) return;

        // Find source workout
        const sourceWorkout = block.workouts.find(
          (w) => w.id === sourceWorkoutId
        );
        if (!sourceWorkout) return;

        // Find target workout (this workout)
        const targetWorkout = block.workouts.find(
          (w) => w.id === targetWorkoutId
        );
        if (!targetWorkout) return;

        const sourceExercise = sourceWorkout.exercises.find(
          (ex) => ex?.id === exerciseId
        );
        if (!sourceExercise) return;

        const protocolSlot = normalizeProtocolSlot(sourceExercise.protocolSlot);
        const pack =
          protocolSlot && Array.isArray(sourceWorkout.exercises)
            ? sourceWorkout.exercises.filter(
                (ex) => ex?.protocolSlot === protocolSlot
              )
            : [sourceExercise];
        const pairedExercise =
          pack.find((ex) => ex?.id !== sourceExercise?.id) ?? null;

        // Check if target workout would exceed exercise limit
        const targetExerciseCount = targetWorkout?.exercises?.length ?? 0;
        const exercisesToAdd = pack.length;
        if (
          targetExerciseCount + exercisesToAdd >
          WorkoutLimits.MAX_EXERCISES_PER_WORKOUT
        ) {
          showToast("maxExercisesPerWorkoutReached-string", {
            type: "warning",
            title: "maxExercisesPerWorkoutReached-string",
            description: "maxExercisesPerWorkoutDescription-string",
            variables: { limit: WorkoutLimits.MAX_EXERCISES_PER_WORKOUT },
          });
          return;
        }

        if (pack.length > 1 && sourceWorkout.removeExercisesByIds) {
          const removed = sourceWorkout.removeExercisesByIds(
            pack.map((ex) => ex?.id)
          );
          if (!removed.length) return;
          if (targetWorkout.receiveExercisePack) {
            targetWorkout.receiveExercisePack(removed, targetIndex);
          } else {
            removed.forEach((exercise, idx) => {
              targetWorkout.receiveExercise(exercise, targetIndex + idx);
            });
          }
        } else {
          // Remove from source
          const exercise = sourceWorkout.removeExercise(exerciseId);
          if (!exercise) return;

          // Add to target
          targetWorkout.receiveExercise(exercise, targetIndex);
        }

        // Persist changes
        persistBlockChange?.();

        if (pairedExercise?.name) {
          const firstPosition =
            getExercisePosition(targetWorkout, sourceExercise?.id) ??
            targetIndex + 1;
          const secondPosition =
            getExercisePosition(targetWorkout, pairedExercise?.id) ??
            firstPosition + 1;
          const isSameWeek = sourceWorkout?.week === targetWorkout?.week;
          showToast("exerciseMovedToastTitlePlural-string", {
            type: "info",
            title: "exerciseMovedToastTitlePlural-string",
            description: isSameWeek
              ? "exerciseMovedToastDescriptionPluralSameWeek-string"
              : "exerciseMovedToastDescriptionPluralCrossWeek-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: {
              firstExerciseName: getExerciseName(sourceExercise),
              secondExerciseName: getExerciseName(pairedExercise),
              workoutName: getWorkoutName(targetWorkout),
              weekLabel: getWeekLabel(targetWorkout?.week),
              firstPosition,
              secondPosition,
            },
          });
        } else {
          const position =
            getExercisePosition(targetWorkout, sourceExercise?.id) ??
            targetIndex + 1;
          const isSameWeek = sourceWorkout?.week === targetWorkout?.week;
          showToast("exerciseMovedToastTitle-string", {
            type: "info",
            title: "exerciseMovedToastTitle-string",
            description: isSameWeek
              ? "exerciseMovedToastDescriptionSameWeek-string"
              : "exerciseMovedToastDescriptionCrossWeek-string",
            duration: BlockPlanificationConstants.TOAST_DURATION_MS,
            variables: {
              exerciseName: getExerciseName(sourceExercise),
              workoutName: getWorkoutName(targetWorkout),
              weekLabel: getWeekLabel(targetWorkout?.week),
              position,
            },
          });
        }
      },
      [
        block,
        persistBlockChange,
        showToast,
        getExerciseName,
        getWorkoutName,
        getWeekLabel,
        getExercisePosition,
        t,
      ]
    );

    // Handle moving workout up
    const handleMoveWorkoutUp = useCallback(() => {
      if (
        !canMoveUp ||
        !block?.workouts ||
        !workout ||
        workout.week === null ||
        workout.week === undefined
      )
        return;

      // Get all workouts from the same week, sorted by order
      const sameWeekWorkouts = block.workouts
        .filter((w) => w.week === workout.week)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const index = sameWeekWorkouts.findIndex((w) => w.id === workout.id);
      if (index === -1 || index === 0) return; // Already at the top

      const newIndex = index - 1;

      // Swap the workouts in the array
      [sameWeekWorkouts[index], sameWeekWorkouts[newIndex]] = [
        sameWeekWorkouts[newIndex],
        sameWeekWorkouts[index],
      ];

      // Update the order of all workouts based on their new positions
      sameWeekWorkouts.forEach((w, idx) => {
        w.order = idx;
        w.setProperty("hasChanged", true);
      });

      persistBlockChange?.();
    }, [canMoveUp, block, workout, persistBlockChange]);

    // Handle moving workout down
    const handleMoveWorkoutDown = useCallback(() => {
      if (
        !canMoveDown ||
        !block?.workouts ||
        !workout ||
        workout.week === null ||
        workout.week === undefined
      )
        return;

      // Get all workouts from the same week, sorted by order
      const sameWeekWorkouts = block.workouts
        .filter((w) => w.week === workout.week)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const index = sameWeekWorkouts.findIndex((w) => w.id === workout.id);
      if (index === -1 || index >= sameWeekWorkouts.length - 1) return; // Already at the bottom

      const newIndex = index + 1;

      // Swap the workouts in the array
      [sameWeekWorkouts[index], sameWeekWorkouts[newIndex]] = [
        sameWeekWorkouts[newIndex],
        sameWeekWorkouts[index],
      ];

      // Update the order of all workouts based on their new positions
      sameWeekWorkouts.forEach((w, idx) => {
        w.order = idx;
        w.setProperty("hasChanged", true);
      });

      persistBlockChange?.();
    }, [canMoveDown, block, workout, persistBlockChange]);

    // Handle delete workout
    const handleDeleteWorkoutClick = useCallback(() => {
      if (!workout) return;

      // If workout has no exercises, delete directly
      if (!hasExercises) {
        handleDeleteWorkout(workout);
      } else {
        // If workout has exercises, show confirmation alert
        setShowingDeleteAlert(true);
      }
    }, [workout, hasExercises, handleDeleteWorkout]);

    const confirmDeleteWorkout = useCallback(() => {
      if (!workout) return;
      handleDeleteWorkout(workout);
      setShowingDeleteAlert(false);
    }, [workout, handleDeleteWorkout]);

    const startEditingWorkoutName = () => {
      const initial = typeof workout?.name === "string" ? workout.name : "";
      workoutNameOriginalRef.current = initial;
      workoutNameDraftRef.current = initial;
      setWorkoutNameDraft(initial);
      skipNextWorkoutNameCommitRef.current = false;
      skipNextWorkoutNameBlurCommitRef.current = false;
      setIsEditingWorkoutName(true);
    };

    const commitWorkoutName = () => {
      skipNextWorkoutNameBlurCommitRef.current = true;
      if (pendingWorkoutNameBlurTimerRef.current) {
        clearTimeout(pendingWorkoutNameBlurTimerRef.current);
        pendingWorkoutNameBlurTimerRef.current = null;
      }

      if (skipNextWorkoutNameCommitRef.current) {
        skipNextWorkoutNameCommitRef.current = false;
        setIsEditingWorkoutName(false);
        return;
      }
      const next =
        typeof workoutNameDraftRef.current === "string"
          ? workoutNameDraftRef.current.trim()
          : "";
      if (next && next !== workout?.name) {
        workout?.setProperty?.("name", next);
        workout?.setProperty?.("hasChanged", true);
        persistBlockChange?.();
      }
      setIsEditingWorkoutName(false);
    };

    const cancelWorkoutNameEdit = () => {
      skipNextWorkoutNameCommitRef.current = true;
      skipNextWorkoutNameBlurCommitRef.current = true;
      if (pendingWorkoutNameBlurTimerRef.current) {
        clearTimeout(pendingWorkoutNameBlurTimerRef.current);
        pendingWorkoutNameBlurTimerRef.current = null;
      }
      const original =
        typeof workoutNameOriginalRef.current === "string"
          ? workoutNameOriginalRef.current
          : "";
      workoutNameDraftRef.current = original;
      setWorkoutNameDraft(original);
      setIsEditingWorkoutName(false);
      workoutNameInputRef.current?.blur?.();
    };

    const handleWorkoutNameChange = (value) => {
      workoutNameDraftRef.current = value;
      setWorkoutNameDraft(value);
    };

    const handleWorkoutNameBlur = () => {
      if (skipNextWorkoutNameBlurCommitRef.current) {
        skipNextWorkoutNameBlurCommitRef.current = false;
        return;
      }

      // On web, blur can fire before the Cancel button's onPress.
      // Defer commit enough so Cancel/ESC can reliably set skip flags / clear timers.
      if (pendingWorkoutNameBlurTimerRef.current) {
        clearTimeout(pendingWorkoutNameBlurTimerRef.current);
      }

      pendingWorkoutNameBlurTimerRef.current = setTimeout(() => {
        pendingWorkoutNameBlurTimerRef.current = null;
        commitWorkoutName();
      }, 120);
    };

    const handleWorkoutNameKey = (e) => {
      const key = e?.nativeEvent?.key ?? e?.key;
      if (key === "Enter") {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        commitWorkoutName();
        return;
      }

      if (key === "Escape") {
        // Prevent any default behavior and exit rename mode.
        e?.preventDefault?.();
        e?.stopPropagation?.();
        skipNextWorkoutNameCommitRef.current = true;
        skipNextWorkoutNameBlurCommitRef.current = true;
        cancelWorkoutNameEdit();
      }
    };

    // iOS mobile: Use useLayoutEffect for synchronous focus
    useLayoutEffect(() => {
      if (!isEditingWorkoutName) return;
      // Only use useLayoutEffect for iOS mobile
      if (!isMobile || !isIOS) return;
      try {
        const input = workoutNameInputRef.current;
        if (input) {
          input.focus?.();
        }
      } catch (e) {
        // no-op
      }
    }, [isEditingWorkoutName, isMobile, isIOS]);

    // Desktop/other: Use useEffect with setTimeout
    useEffect(() => {
      if (!isEditingWorkoutName) return;
      // Skip useLayoutEffect path for iOS mobile (handled above)
      if (isMobile && isIOS) return;
      // React Native Web: focusing after mount avoids flaky focus.
      const id = setTimeout(() => {
        workoutNameInputRef.current?.focus?.();
      }, 0);
      return () => clearTimeout(id);
    }, [isEditingWorkoutName, isMobile, isIOS]);

    useEffect(() => {
      if (!isEditingWorkoutName) return;
      if (typeof window === "undefined") return;

      const onKeyDown = (e) => {
        if (e.key === "Escape") {
          cancelWorkoutNameEdit();
        }
      };

      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [isEditingWorkoutName]);

    // Keyboard shortcut for Add Exercise (Shift+Enter)
    useEffect(() => {
      if (!isEditMode) return;
      if (typeof window === "undefined") return;

      const isEditableTarget = (target) => {
        if (!target) return false;
        const tagName = String(target.tagName || "").toLowerCase();
        if (tagName === "input" || tagName === "textarea") return true;
        return target.isContentEditable === true;
      };

      const handleKeyDown = (e) => {
        if (e.key !== "Enter" || e.shiftKey !== true) return;
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        e.stopPropagation();
        startAddExercise();
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isEditMode, startAddExercise]);

    // Clear edit mode if workout becomes empty (no exercises)
    useEffect(() => {
      if (!workout) return;
      if (
        !hasExercises &&
        block?.blockViewEditingWorkoutId === String(workout?.id).toLowerCase()
      ) {
        block?.clearBlockViewEditingWorkoutId?.();
      }
    }, [hasExercises, workout, block]);

    return (
      <View key={workout.id} style={styles.workoutWrapper}>
        <View
          style={[
            styles.workoutCard,
            isWeekHidden && styles.workoutCardHidden,
            !isExercisesExpanded && styles.workoutCardCollapsed,
          ]}
        >
          {isWeekHidden && <View style={styles.hiddenOverlay} />}
          <View style={styles.workoutHeader}>
            <View style={styles.workoutHeaderLeft}>
              <View style={styles.workoutTitleRow}>
                <View
                  style={[
                    styles.workoutTitlePressArea,
                    isEditingWorkoutName && { flex: 1, minWidth: 0 },
                  ]}
                >
                  {isEditingWorkoutName ? (
                    <TextInput
                      ref={workoutNameInputRef}
                      style={[
                        styles.workoutTitleInput,
                        { flex: 1, minWidth: 0 },
                        isMobile && isIOS && { fontSize: 16 },
                      ]}
                      value={workoutNameDraft}
                      onChangeText={handleWorkoutNameChange}
                      onKeyDown={handleWorkoutNameKey}
                      onKeyPress={handleWorkoutNameKey}
                      onSubmitEditing={commitWorkoutName}
                      onBlur={handleWorkoutNameBlur}
                      blurOnSubmit={true}
                      returnKeyType="done"
                      numberOfLines={1}
                    />
                  ) : (
                    <Text style={styles.workoutTitle} numberOfLines={1}>
                      {workout.name}
                    </Text>
                  )}

                  {!isEditingWorkoutName &&
                    (() => {
                      const label = workout?.date
                        ? formatWorkoutDate(workout.date)
                        : null;
                      if (!label) return null;
                      return (
                        <Text
                          style={styles.workoutDateInlineText}
                          numberOfLines={1}
                        >
                          · {label}
                        </Text>
                      );
                    })()}
                </View>

                {!isEditingWorkoutName && isEditMode && (
                  <Tooltip content={t("done-string")} placement="top">
                    <HoverableTouchableOpacity
                      style={styles.editTitleButton}
                      hoverStyle={styles.iconBtnHover}
                      onPress={() => {
                        cancelWorkoutNameEdit();
                        clearExerciseFocusRequest();
                        exitWorkoutEditMode();
                      }}
                      title={t("done-string")}
                    >
                      <Ionicons
                        name="checkmark"
                        size={16}
                        color={Colors.bubbleColor}
                      />
                    </HoverableTouchableOpacity>
                  </Tooltip>
                )}

                {isEditingWorkoutName ? (
                  <>
                    <Tooltip content={t("cancel-string")} placement="top">
                      <HoverableTouchableOpacity
                        style={styles.editTitleButton}
                        hoverStyle={styles.iconBtnHover}
                        onPressIn={() => {
                          skipNextWorkoutNameCommitRef.current = true;
                          skipNextWorkoutNameBlurCommitRef.current = true;
                          if (pendingWorkoutNameBlurTimerRef.current) {
                            clearTimeout(
                              pendingWorkoutNameBlurTimerRef.current
                            );
                            pendingWorkoutNameBlurTimerRef.current = null;
                          }
                        }}
                        onPress={cancelWorkoutNameEdit}
                        title={t("cancel-string")}
                      >
                        <Feather name="x" size={14} color={Colors.grayColor} />
                      </HoverableTouchableOpacity>
                    </Tooltip>

                    <Tooltip content={t("save-string")} placement="top">
                      <HoverableTouchableOpacity
                        style={styles.editTitleButton}
                        hoverStyle={styles.iconBtnHover}
                        onPressIn={() => {
                          // Avoid double-committing via onBlur after click.
                          skipNextWorkoutNameCommitRef.current = false;
                        }}
                        onPress={commitWorkoutName}
                        title={t("save-string")}
                      >
                        <Ionicons
                          name="checkmark"
                          size={14}
                          color={Colors.bubbleColor}
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip
                    content={t("editWorkoutName-string")}
                    placement="top"
                  >
                    <HoverableTouchableOpacity
                      style={styles.editTitleButton}
                      hoverStyle={styles.iconBtnHover}
                      onPress={() => {
                        startEditingWorkoutName();
                      }}
                      onLongPress={() => {
                        // Keep existing edit-mode behavior accessible without changing UX surface.
                        // Long-press selects the workout, same as previous tap behavior.
                        if (!isEditMode) {
                          block?.setBlockViewEditingWorkoutId?.(workout.id);
                          clearExerciseFocusRequest();
                        }
                      }}
                      title={t("editWorkoutName-string")}
                    >
                      <Feather
                        name="edit-2"
                        size={12}
                        color={Colors.grayColor}
                      />
                    </HoverableTouchableOpacity>
                  </Tooltip>
                )}

                {!isEditingWorkoutName && (
                  <Tooltip
                    content={t("copyWorkoutExercises-string")}
                    placement="top"
                  >
                    <HoverableTouchableOpacity
                      style={[
                        styles.editTitleButton,
                        copyWorkoutDisabled && styles.copyWorkoutDisabled,
                      ]}
                      hoverStyle={styles.iconBtnHover}
                      onPress={() => {
                        if (copyWorkoutDisabled) return;
                        coach?.copyExercisesFromWorkout?.(workout);
                        const workoutName = getWorkoutName(workout);
                        showToast("exercisesCopied-string", {
                          type: "info",
                          title: "exercisesCopied-string",
                          description: "exercisesCopiedDescription-string",
                          duration:
                            BlockPlanificationConstants.TOAST_DURATION_MS,
                          variables: { workoutName },
                        });
                      }}
                      title={t("copyWorkoutExercises-string")}
                    >
                      <Ionicons
                        name="copy-outline"
                        size={14}
                        color={Colors.grayColor}
                      />
                    </HoverableTouchableOpacity>
                  </Tooltip>
                )}

                {!isEditingWorkoutName && (
                  <FloatingMenu
                    placement="bottom-end"
                    width={220}
                    items={workoutMoreMenuItems}
                    onOpenChange={setIsWorkoutMenuOpen}
                    trigger={
                      <Tooltip
                        content={t("moreOptions-string")}
                        placement="top"
                        disabled={isWorkoutMenuOpen}
                      >
                        <View
                          style={[
                            styles.editTitleButton,
                            isMoreHovered ? styles.iconBtnHover : null,
                          ]}
                          title={t("moreOptions-string")}
                          onMouseEnter={() =>
                            !isMobile && setIsMoreHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsMoreHovered(false)
                          }
                        >
                          <Ionicons
                            name="ellipsis-horizontal"
                            size={16}
                            color={
                              isMoreHovered
                                ? Colors.darkBubbleColor
                                : Colors.grayColor
                            }
                          />
                        </View>
                      </Tooltip>
                    }
                    renderItem={({ item, close }) => (
                      <HoverableTouchableOpacity
                        style={styles.workoutMoreMenuItemRow}
                        hoverStyle={styles.workoutMoreMenuItemHover}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          handleWorkoutMenuItemAction(item.key);
                          close();
                        }}
                        title={item.label}
                      >
                        {item?.icon?.type === "ionicons" ? (
                          <Ionicons
                            name={item.icon.name}
                            size={14}
                            color={Colors.darkgrayColor}
                          />
                        ) : null}
                        <Text style={styles.workoutMoreMenuItemText}>
                          {item.label}
                        </Text>
                      </HoverableTouchableOpacity>
                    )}
                  />
                )}
              </View>
            </View>
          </View>

          <View
            style={[
              styles.workoutBody,
              !isExercisesExpanded && styles.workoutBodyCollapsed,
              isMobile && { paddingHorizontal: 4, paddingBottom: 4 },
              { flexDirection: "column" },
            ]}
          >
            {!hasExercises && !isAddingExercise && (
              <EmptyWorkoutDropZone
                workoutId={workout.id}
                workoutWeek={workout?.week ?? null}
                style={styles.emptyDropZone}
              >
                <EmptyView
                  onAddExercise={startAddExercise}
                  onPasteExercises={() => handlePasteExercise?.(workout)}
                  pasteButtonLabel={pasteButtonLabel}
                  copiedExercisesCount={copiedExercises.length}
                  t={t}
                />
              </EmptyWorkoutDropZone>
            )}

            {(hasExercises || isAddingExercise) && (
              <View
                style={[
                  styles.exercisesList,
                  { gap: isEditMode && isExercisesExpanded ? 0 : 4 },
                  isMobile && { paddingTop: 4 },
                ]}
              >
                {showOnlyProtocolSlots
                  ? onlySlotsDisplayItems?.map((item) => {
                      if (item.type === "placeholder") {
                        return (
                          <View
                            key={item.key}
                            style={styles.hiddenExercisesRow}
                          >
                            <Text style={styles.hiddenExercisesText}>
                              {t("hiddenExercisesCount-string", {
                                count: item.count,
                              })}
                            </Text>
                          </View>
                        );
                      }

                      const exercise = item.exercise;

                      return (
                        <DraggableExercise
                          key={exercise?.id ?? exercise?.name}
                          exercise={exercise}
                          idx={item.index}
                          workoutId={workout.id}
                          workoutWeek={workout?.week ?? null}
                          gap={isEditMode && isExercisesExpanded ? 0 : 4}
                          isEditMode={isEditMode}
                        >
                          {({ dragListeners, dragHandleRef, isDragging }) => (
                            <ExerciseCard
                              exercise={exercise}
                              block={block}
                              isEditMode={isEditMode}
                              onCopyExercise={handleCopyExercise}
                              showExerciseHistory={showExerciseHistory}
                              onShowProtocolsGuide={() => {
                                if (!coach?.setProperty) return;
                                coach.setProperty(
                                  "showingProtocolsGuideBlockView",
                                  false
                                );
                                setTimeout(() => {
                                  coach.setProperty(
                                    "showingProtocolsGuideBlockView",
                                    true
                                  );
                                }, 0);
                              }}
                              onPressCard={() => {
                                requestExerciseEditFocus(
                                  exercise,
                                  "protocol",
                                  undefined,
                                  false
                                );
                              }}
                              onRequestEditFocus={(field, serieId) =>
                                requestExerciseEditFocus(
                                  exercise,
                                  field,
                                  serieId,
                                  true
                                )
                              }
                              focusField={
                                focusRequest &&
                                focusRequest.exerciseId === exercise.id
                                  ? focusRequest.field
                                  : null
                              }
                              focusSerieId={
                                focusRequest &&
                                focusRequest.exerciseId === exercise.id
                                  ? focusRequest.serieId
                                  : null
                              }
                              focusFromDirectClick={
                                focusRequest &&
                                focusRequest.exerciseId === exercise.id
                                  ? focusRequest.fromDirectClick
                                  : false
                              }
                              onFocusHandled={clearExerciseFocusRequest}
                              dragHandleListeners={dragListeners}
                              dragHandleRef={dragHandleRef}
                              showSetPriority={showSetPriority}
                              rirOrRpe={rirOrRpe}
                              exercisesExpanded={exercisesExpanded}
                            />
                          )}
                        </DraggableExercise>
                      );
                    })
                  : sortedExercises.map((exercise, idx) => (
                      <DraggableExercise
                        key={exercise?.id ?? exercise?.name}
                        exercise={exercise}
                        idx={idx}
                        workoutId={workout.id}
                        workoutWeek={workout?.week ?? null}
                        gap={isEditMode && isExercisesExpanded ? 0 : 4}
                        isEditMode={isEditMode}
                      >
                        {({ dragListeners, dragHandleRef, isDragging }) => (
                          <ExerciseCard
                            exercise={exercise}
                            block={block}
                            isEditMode={isEditMode}
                            onCopyExercise={handleCopyExercise}
                            showExerciseHistory={showExerciseHistory}
                            onShowProtocolsGuide={() => {
                              if (!coach?.setProperty) return;
                              coach.setProperty(
                                "showingProtocolsGuideBlockView",
                                false
                              );
                              setTimeout(() => {
                                coach.setProperty(
                                  "showingProtocolsGuideBlockView",
                                  true
                                );
                              }, 0);
                            }}
                            onPressCard={() => {
                              requestExerciseEditFocus(
                                exercise,
                                "protocol",
                                undefined,
                                false
                              );
                            }}
                            onRequestEditFocus={(field, serieId) =>
                              requestExerciseEditFocus(
                                exercise,
                                field,
                                serieId,
                                true
                              )
                            }
                            focusField={
                              focusRequest &&
                              focusRequest.exerciseId === exercise.id
                                ? focusRequest.field
                                : null
                            }
                            focusSerieId={
                              focusRequest &&
                              focusRequest.exerciseId === exercise.id
                                ? focusRequest.serieId
                                : null
                            }
                            focusFromDirectClick={
                              focusRequest &&
                              focusRequest.exerciseId === exercise.id
                                ? focusRequest.fromDirectClick
                                : false
                            }
                            onFocusHandled={clearExerciseFocusRequest}
                            dragHandleListeners={dragListeners}
                            dragHandleRef={dragHandleRef}
                            showSetPriority={showSetPriority}
                            rirOrRpe={rirOrRpe}
                            exercisesExpanded={exercisesExpanded}
                          />
                        )}
                      </DraggableExercise>
                    ))}

                {isAddingExercise && (
                  <>
                    <ExerciseSelectionPlaceholder
                      onCancel={cancelAddExercise}
                      onOpen={openAddExerciseSearch}
                      setAnchorNode={(node) => {
                        // React Native Web: store the DOM node used as FloatingUI reference.
                        setAddExerciseAnchorNode(node || null);
                      }}
                    />
                  </>
                )}
              </View>
            )}

            {/* Trailing spacer - fills remaining space between exercises and footer */}
            {/* Only renders if there are exercises and not adding one */}
            {hasExercises && !isAddingExercise && (
              <View style={styles.trailingSpacer}>
                {/* Trailing drop zone - absolutely positioned within spacer */}
                <TrailingDropZone
                  workoutId={workout.id}
                  workoutWeek={workout?.week ?? null}
                  exerciseCount={workout?.exercises?.length ?? 0}
                  gap={isEditMode ? 0 : 4}
                />
              </View>
            )}
          </View>

          <View
            style={[
              styles.workoutFooter,
              isMobile && { paddingHorizontal: 4, paddingVertical: 4 },
            ]}
          >
            <View style={styles.footerLeft}>
              <HoverableTouchableOpacity
                style={styles.footerAddExerciseBtn}
                hoverStyle={styles.btnHover}
                onPress={startAddExercise}
                title={t("addExercise-string")}
              >
                <Ionicons name="add" size={16} color={Colors.bubbleColor} />
                <Text style={styles.footerAddExerciseText}>
                  {t("addExercise-string")}
                </Text>
                {isEditMode && !isMobile && (
                  <Text style={styles.footerKeyboardHint}>⇧↵</Text>
                )}
              </HoverableTouchableOpacity>

              {copiedExercises.length > 0 && (
                <Tooltip content={pasteButtonLabel} placement="top">
                  <HoverableTouchableOpacity
                    style={styles.footerPasteExerciseBtn}
                    hoverStyle={styles.btnHover}
                    onPress={() => handlePasteExercise(workout)}
                    title={pasteButtonLabel}
                  >
                    <Ionicons
                      name="clipboard-outline"
                      size={16}
                      color={Colors.bubbleColor}
                    />
                  </HoverableTouchableOpacity>
                </Tooltip>
              )}
            </View>

            <View style={styles.footerRight}>
              <Tooltip content={t("moveWorkoutUp-string")} placement="top">
                <HoverableTouchableOpacity
                  style={styles.footerActionBtn}
                  hoverStyle={styles.footerIconHover}
                  onPress={handleMoveWorkoutUp}
                  title={t("moveWorkoutUp-string")}
                >
                  <View style={!canMoveUp ? { opacity: 0.4 } : null}>
                    <Ionicons
                      name="chevron-up"
                      size={16}
                      color={Colors.grayColor}
                    />
                  </View>
                </HoverableTouchableOpacity>
              </Tooltip>
              <Tooltip content={t("moveWorkoutDown-string")} placement="top">
                <HoverableTouchableOpacity
                  style={styles.footerActionBtn}
                  hoverStyle={styles.footerIconHover}
                  onPress={handleMoveWorkoutDown}
                  title={t("moveWorkoutDown-string")}
                >
                  <View style={!canMoveDown ? { opacity: 0.4 } : null}>
                    <Ionicons
                      name="chevron-down-outline"
                      size={16}
                      color={Colors.grayColor}
                    />
                  </View>
                </HoverableTouchableOpacity>
              </Tooltip>

              <Tooltip
                content={t("deleteWorkoutTooltip-string")}
                placement="top"
              >
                <HoverableTouchableOpacity
                  style={styles.footerActionBtn}
                  hoverStyle={styles.footerTrashIconHover}
                  title={t("deleteWorkoutTooltip-string")}
                  onPress={handleDeleteWorkoutClick}
                >
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    color={Colors.redColor}
                  />
                </HoverableTouchableOpacity>
              </Tooltip>
            </View>
          </View>
        </View>
        {showingDeleteAlert && (
          <CustomAlert
            isVisible={showingDeleteAlert}
            onClose={() => setShowingDeleteAlert(false)}
            title="deleteWorkoutAlertTitle-string"
            text="deleteWorkoutAlert-string"
            action={confirmDeleteWorkout}
            specialCTA="delete-string"
          />
        )}
      </View>
    );
  }
);

export default WorkoutCard;

const styles = StyleSheet.create({
  workoutWrapper: {},
  workoutCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    minHeight: BlockPlanificationConstants.WORKOUT_MIN_HEIGHT,
    // overflow: "hidden",
  },
  workoutCardCollapsed: {
    minHeight: BlockPlanificationConstants.WORKOUT_MIN_HEIGHT / 2,
  },
  workoutHeader: {
    height: BlockPlanificationConstants.WORKOUT_HEADER_HEIGHT,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderTopRightRadius: 14,
    borderTopLeftRadius: 14,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderBottomColor: Colors.borderColor,
    backgroundColor: Colors.lightGrayColor,
  },
  workoutHeaderLeft: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  workoutTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  workoutTitlePressArea: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 2,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  workoutTitle: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
    flexShrink: 1,
  },
  workoutTitleInput: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
    padding: 0,
    outlineStyle: "none",
  },
  workoutDateInlineText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
    flexShrink: 0,
  },
  editTitleButton: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnHover: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 6,
  },
  copyWorkoutDisabled: {
    opacity: 0.4,
  },
  workoutBody: {
    position: "relative",
    paddingHorizontal: 8,
    paddingBottom: 8,
    minHeight: 120,
    justifyContent: "flex-start",
    alignItems: "stretch",
    flexGrow: 1,
  },
  workoutBodyCollapsed: {
    minHeight: 60,
  },
  exercisesList: {
    gap: 8,
    // marginHorizontal: -4,
    paddingTop: 8,
  },
  hiddenExercisesRow: {
    paddingVertical: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  hiddenExercisesText: {
    fontSize: 11,
    fontWeight: "400",
    fontStyle: "italic",
    color: Colors.grayColor,
  },
  workoutFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: Colors.borderColor,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
    marginTop: "auto",
  },
  footerLeft: {
    flexDirection: "row",
    gap: 4,
    alignItems: "center",
  },
  footerRight: {
    flexDirection: "row",
    gap: 4,
  },
  footerActionBtn: {
    padding: 6,
    borderRadius: 8,
  },
  footerIconHover: {
    backgroundColor: Colors.hoveredGray,
    borderRadius: 8,
  },
  footerTrashIconHover: {
    backgroundColor: "rgba(240, 68, 56, 0.1)",
    borderRadius: 8,
  },
  footerAddExerciseBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
  },
  btnHover: {
    backgroundColor: Colors.hoveredGray,
  },
  footerAddExerciseText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  footerPasteExerciseBtn: {
    padding: 6,
    borderRadius: 8,
  },
  footerKeyboardHint: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
    marginLeft: 0,
  },
  addExerciseDisabled: {
    opacity: 0.5,
  },

  emptyContainer: {
    flex: 1,
    minHeight: 120,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 8,
  },
  emptyIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: Colors.bubbleColor,
    opacity: 0.08,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  emptySubtitle: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  emptyActionsRow: {
    flexDirection: "row",
    gap: 4,
    marginTop: 6,
  },
  emptyPrimaryBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
  },
  emptyPrimaryBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  emptySecondaryBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
  },
  emptySecondaryBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  workoutMoreMenuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  workoutMoreMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  workoutMoreMenuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  emptyDisabled: {
    opacity: 0.6,
  },
  // Drop zone styles
  emptyDropZone: {
    flex: 1,
    minHeight: 120,
    marginTop: 8,
    borderRadius: 8,
  },
  // Spacer that fills remaining space between exercises and footer
  // Uses flex: 1 to take up available space, collapses to 0 if no space
  trailingSpacer: {
    flex: 1,
    minHeight: 0, // Allow collapsing to 0 if no space
    position: "relative", // Container for absolutely positioned drop zone
  },
  // Zero-height wrapper - no layout impact
  trailingDropWrapper: {
    height: 0,
    position: "relative",
    overflow: "visible",
  },
  // Hit area extends below the exercises
  trailingDropHitArea: {
    position: "absolute",
    top: 0,
    left: -4,
    right: -4,
    height: 80, // Fixed hit area - reasonable drop target
  },
  dropZoneActive: {
    backgroundColor: Colors.lightGrayColor, // 12 is ~7% opacity in hex
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    borderStyle: "dashed",
    // marginTop:-4
  },
  workoutCardHidden: {
    opacity: 0.92,
    position: "relative",
    overflow: "hidden",
  },
  hiddenOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    pointerEvents: "none",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    borderRadius: 14,
    backgroundImage:
      "repeating-linear-gradient(45deg, transparent, transparent 16px, rgba(0, 0, 0, 0.02) 16px, rgba(0, 0, 0, 0.02) 32px)",
  },
});
